// Presentational modal for Free-tier wall hits — per-type Lite copy + interest CTA.
import { useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { useAuth } from '@/features/auth/useAuth';
import { useToast } from '@/features/ui/hooks/useToast';
import { useT } from '@/i18n/useT';
import {
  logWallHit,
  logWallUpgradeClick,
  logWallUpgradeDismiss,
} from '@/services/analyticsService';
import { registerWallInterest } from '@/services/userService';

/**
 * @typedef {import('@/services/analyticsService.types').WallType} WallType
 */

/**
 * @param {{ open: boolean, onClose: () => void, wallType: WallType }} props
 */
const WallModal = ({ open, onClose, wallType }) => {
  const t = useT();
  const { user } = useAuth();
  const { showToast } = useToast();

  useEffect(() => {
    if (!open) return;
    logWallHit({ wall_type: wallType });
  }, [open, wallType]);

  const handleDismiss = useCallback(() => {
    logWallUpgradeDismiss({ wall_type: wallType });
    onClose();
  }, [wallType, onClose]);

  const handleRegisterInterest = useCallback(async () => {
    logWallUpgradeClick({ wall_type: wallType });
    if (user?.uid) {
      try {
        await registerWallInterest(user.uid, wallType);
      } catch (error) {
        console.error('[WallModal] Failed to register wall interest:', error);
      }
    }
    showToast(t('wall.interestThankYou'));
    onClose();
  }, [user?.uid, wallType, showToast, t, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t(`wall.${wallType}.title`)}
      </DialogTitle>
      <DialogContent>
        <DialogContentText color="text.secondary">
          {t(`wall.${wallType}.body`)}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button color="inherit" onClick={handleDismiss}>
          {t('wall.understood')}
        </Button>
        <Button
          onClick={handleRegisterInterest}
          variant="contained"
          disableElevation
        >
          {t('wall.registerInterest')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WallModal;
